/**
 * @author      yewon@zumper.com (Yewon Lee)
 * @copyright   Copyright (c) 2018, Zumper
 * @description removeSavedSearch Thunk
 */

import { search } from '@zcode/nr/es/shared/api'
import { selectIsLoggedIn } from '@zcode/nr/es/shared/modules/user/selectors'
import Marketing from '@zcode/nr/es/shared/utils/marketing'
import { ALERT_DELETE } from '@zcode/nr/es/shared/utils/marketing/constants/event'
import type { GetState } from '@zcode/state-type'
import type { Dispatch } from 'redux'
import { addErrorButterBar, addSuccessButterBar } from '../../../../actions/butter.action'
import { successRemoving } from '../../../../modules/savedSearchesView/actions'
import { errorRemoving } from '../../../../modules/savedSearchesView/actions'
import { beginRemoving } from '../../../../modules/savedSearchesView/actions'
import { deselect, hideSavedSearchesView } from '../../../savedSearchesView/actions/index'
import { selectSelectedId } from '../../../savedSearchesView/selectors/index'
import { default as loadSavedSearches } from '../../../savedSearchesView/thunks/loadSavedSearches'

interface Props {
  searchId: number
  isLast?: boolean
  userId?: number
  email?: string | null
}

export const removeSavedSearch =
  ({ searchId, isLast, userId, email }: Props) =>
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState()
    const selected = selectSelectedId(state)
    const isLoggedIn = selectIsLoggedIn(state)

    dispatch(beginRemoving())
    // optimistically removing the savedSearch for better UI transition
    dispatch(successRemoving({ searchId }))
    if (selected) {
      dispatch(deselect())
    }
    if (!isLoggedIn) {
      // if a user is not logged in we can't send a delete request because it will fail
      // so we only remove the item from the state
      return
    }
    search
      .remove({ searchId })
      .then(() => {
        if (isLast) {
          dispatch(hideSavedSearchesView())
        }
        dispatch(
          addSuccessButterBar({
            isSearchButter: true,
            message: 'Alert deleted',
          })
        )
        Marketing.track(ALERT_DELETE, {
          alert_id: searchId,
          customer_id: userId,
          email,
          event: ALERT_DELETE,
        })
      })
      .catch((error: unknown) => {
        dispatch(
          addErrorButterBar({
            isSearchButter: true,
            message: 'Something went wrong, try again later.',
          })
        )
        dispatch(errorRemoving(error))
        // we need to refetch savedSearches because we optimistically removed the savedSearch
        dispatch(loadSavedSearches()).catch(() => {})
      })
  }

export default removeSavedSearch
